// Stacking-tabs ear geometry for the PCB stator (independent 6-lead winding).
// Pure module — no Angular / DI deps, radians, deterministic, derived constants.
//
// Six plated-through-hole "ears" protrude beyond the board outline so that
// identical boards, each rotated by one electrical period D, mate hole-to-hole:
// board n's − ear seams onto board n+1's + ear at the SAME azimuth, giving a
// pure per-phase series stack (§2/§4 of the stacking-tabs design spec).
//
// Azimuth source of truth: the + ears sit at the REAL drive-pad azimuths — the
// exact `thetaL[0]`/`thetaR[0]` first-coil entry angles that `computeReturnPads`
// derives (same `buildCorners` + `planCoilPolarities` flip logic). The − ears
// sit at exactly + D. Because − = + rotated by D, rotating the whole + hole set
// by D reproduces the − hole set to machine precision — the mating guarantee.

import { StatorParams, driveAzimuth, PHASE_LABEL_LETTERS } from './pcb-stator-geometry';
import { planCoilPolarities } from './pcb-stator-polarity';

/** Radial height of each ear beyond the board outer radius (mm). */
export const EAR_RADIAL_MM = 6;
/** Angular width of each ear's annular sector (degrees). */
export const EAR_ARC_DEG = 10;
/** Plated-through-hole drill diameter for the ear joint holes (mm). */
export const EAR_HOLE_DRILL_MM = 1.0;

/** Symmetric angular offsets (degrees) of the three ear holes about the ear
 *  centerline, on the ear's mid-radius arc. The symmetry is load-bearing: it
 *  makes a rotated + ear land hole-for-hole on the − ear it mates with. */
const EAR_HOLE_OFFSETS_DEG = [-3, 0, 3] as const;

/** Arc-sampling density (vertices per full 2π) for the spliced outline, matching
 *  the encoder disc's OUTLINE_ARC_SAMPLES house convention. */
const OUTLINE_ARC_SAMPLES = 128;
/** Vertices along each ear's outer arc (both tip corners inclusive). */
const EAR_ARC_SAMPLES = 8;

const DEG = Math.PI / 180;

export interface StackingEar {
  phase: number;                     // 0..numPhases-1
  polarity: 'plus' | 'minus';
  azimuthRad: number;                // ear centerline
  outline: Array<[number, number]>;  // open polyline replacing the outer-circle arc across the ear span
  holes: Array<[number, number]>;    // 3 PTH centers on the mid-radius arc at {−3°, 0°, +3°}
  label: string;                     // 'U+' | 'U−' | ...
}

/** One electrical period: the rotation that maps stage n onto stage n+1.
 *  D = 2π / (numPoles / 2) = 2π / polePairs, so D × polePairs ≡ 0 (mod 2π):
 *  every board in the stack is in electrical phase. */
export function stackRotationRad(params: StatorParams): number {
  return (2 * Math.PI) / (params.numPoles / 2);
}

/** Build one ear's open outline polyline and its three hole centers.
 *  Outline walks base-left → (radial edge out) → outer arc → (radial edge in)
 *  → base-right. The two base points sit exactly on the board outer circle so
 *  the outline splices seamlessly into the circle sampling. */
function buildEar(
  params: StatorParams,
  phase: number,
  polarity: 'plus' | 'minus',
  azimuthRad: number,
): StackingEar {
  const rIn = params.outerRadius;
  const rOut = params.outerRadius + EAR_RADIAL_MM;
  const rMid = params.outerRadius + EAR_RADIAL_MM / 2;
  const half = (EAR_ARC_DEG / 2) * DEG;
  const aL = azimuthRad - half;
  const aR = azimuthRad + half;

  const outline: Array<[number, number]> = [];
  outline.push([rIn * Math.cos(aL), rIn * Math.sin(aL)]);      // base-left, on outer circle
  for (let i = 0; i <= EAR_ARC_SAMPLES; i++) {                 // outer arc, both tips inclusive
    const a = aL + (aR - aL) * (i / EAR_ARC_SAMPLES);
    outline.push([rOut * Math.cos(a), rOut * Math.sin(a)]);
  }
  outline.push([rIn * Math.cos(aR), rIn * Math.sin(aR)]);      // base-right, on outer circle

  const holes: Array<[number, number]> = EAR_HOLE_OFFSETS_DEG.map(off => {
    const a = azimuthRad + off * DEG;
    return [rMid * Math.cos(a), rMid * Math.sin(a)] as [number, number];
  });

  const letter = PHASE_LABEL_LETTERS[phase] ?? String(phase);
  const label = `${letter}${polarity === 'plus' ? '+' : '−'}`;
  return { phase, polarity, azimuthRad, outline, holes, label };
}

/**
 * Six ears (per phase: one + at the drive azimuth, one − at drive + D).
 * `termDims` is accepted for interface parity with `computeReturnPads` and the
 * downstream emitters (entry-pad annular sizing lives in the pad emitter, not
 * here); ear hole centers are pad-size-independent.
 */
export function computeStackingEars(
  params: StatorParams,
  _termDims: { padMm: number; holeMm: number },
): StackingEar[] {
  const flips = planCoilPolarities(params).flip;
  const D = stackRotationRad(params);
  const ears: StackingEar[] = [];
  for (let p = 0; p < params.numPhases; p++) {
    const drive = driveAzimuth(params, p, flips);
    ears.push(buildEar(params, p, 'plus', drive));
    ears.push(buildEar(params, p, 'minus', drive + D));
  }
  return ears;
}

/** Normalize an angle to [0, 2π). */
function norm2pi(a: number): number {
  const t = a % (2 * Math.PI);
  return t < 0 ? t + 2 * Math.PI : t;
}

/** Minimal absolute angular separation between two azimuths, in [0, π]. */
function angularSep(a: number, b: number): number {
  const d = norm2pi(a - b);
  return d > Math.PI ? 2 * Math.PI - d : d;
}

const degStr = (r: number) => `${((norm2pi(r) * 180) / Math.PI).toFixed(2)}°`;

/** First pair of ears whose annular sectors overlap (center gap < ear width),
 *  or null when every ear clears every other. An overlap is exactly the
 *  condition under which the spliced outline would self-intersect (bowtie). */
function firstOverlap(ears: StackingEar[]): [StackingEar, StackingEar] | null {
  const width = EAR_ARC_DEG * DEG;
  for (let i = 0; i < ears.length; i++) {
    for (let j = i + 1; j < ears.length; j++) {
      if (angularSep(ears[i].azimuthRad, ears[j].azimuthRad) < width - 1e-9) {
        return [ears[i], ears[j]];
      }
    }
  }
  return null;
}

export interface EarLayoutAssessment {
  ears: StackingEar[];
  valid: boolean;      // false → ears collide; downstream must drop them (honest flag)
  reasons: string[];   // human-readable, names the colliding azimuths + the derived rule
}

/**
 * Honest collision assessment for a pole/coil combination (the recessDropped /
 * encoder-bands `{valid, reasons}` precedent). The `+` ears sit at the fixed
 * drive azimuths (spaced by the coil pitch 2π/numCoils) and the `−` ears at
 * `+ D`, D = 2π/polePairs. A `+` and a `−` ear coincide — collapsing two
 * sectors into one shorted protrusion — exactly when D is an integer multiple
 * of the coil pitch, i.e.
 *
 *     (2π / polePairs) ≡ 0  (mod 2π / numCoils)   ⟺   numCoils mod polePairs === 0
 *
 * (polePairs divides numCoils). Defaults 6 coils / 4 pole-pairs → 6 mod 4 = 2 ≠ 0
 * → clean; 6 coils / 3 pole-pairs (6 poles) → 0 → collide; 6 coils / 6 pole-pairs
 * (12 poles) → 0 → collide. The check itself is geometric (pairwise sector
 * overlap on the real azimuths, so it also catches any flip-induced coincidence);
 * the derived-rule clause is appended when that exact divisibility holds.
 */
export function assessEarLayout(
  params: StatorParams,
  termDims: { padMm: number; holeMm: number } = { padMm: 1.5, holeMm: 0.8 },
): EarLayoutAssessment {
  const ears = computeStackingEars(params, termDims);
  const reasons: string[] = [];
  const hit = firstOverlap(ears);
  if (hit) {
    const [a, b] = hit;
    const polePairs = params.numPoles / 2;
    const pitchDeg = (360 / params.numCoils).toFixed(2);
    let reason =
      `Ear collision: ${a.label} (${degStr(a.azimuthRad)}) and ${b.label} ` +
      `(${degStr(b.azimuthRad)}) overlap (angular gap ` +
      `${((angularSep(a.azimuthRad, b.azimuthRad) * 180) / Math.PI).toFixed(2)}° ` +
      `< ear width ${EAR_ARC_DEG}°).`;
    if (params.numCoils % polePairs === 0) {
      reason +=
        ` One electrical period D=${degStr(stackRotationRad(params))} is a multiple of the ` +
        `${pitchDeg}° coil pitch (polePairs ${polePairs} divides numCoils ${params.numCoils}), ` +
        `so a + and − ear land on the same azimuth.`;
    }
    reasons.push(reason);
  }
  return { ears, valid: reasons.length === 0, reasons };
}

/** Rotate one point about the origin by `angleRad` — the rigid transform that
 *  maps one stacked board's geometry onto the next (see `stackRotationRad`). */
export function rotatePoint(p: readonly [number, number], angleRad: number): [number, number] {
  const c = Math.cos(angleRad), s = Math.sin(angleRad);
  return [p[0] * c - p[1] * s, p[0] * s + p[1] * c];
}

/** Rotate every point of a polyline (open or closed) about the origin. */
export function rotatePoints(
  points: ReadonlyArray<readonly [number, number]>,
  angleRad: number,
): Array<[number, number]> {
  return points.map(p => rotatePoint(p, angleRad));
}

/**
 * Rotate a full ear set about the origin by `angleRad` — outline, holes, and
 * `azimuthRad` all move together; `phase`/`polarity`/`label` are unchanged (it
 * is still "the same ear", just physically relocated).
 *
 * This is the ghost "next stage" preview transform: rotating a stage's ears by
 * D (`stackRotationRad`) moves its + ears exactly onto where its own − ears
 * already sit (the MATING ALIGNMENT invariant — see the module doc comment
 * and the spec test of the same name), i.e. onto where the BASE board's − ears
 * are. The UI reuses this on the already-computed `earLayoutAssessment.ears`
 * — never recomputed from params — so the ghost can only ever show what the
 * real (memoized) layout actually is.
 */
export function rotateStackingEars(
  ears: ReadonlyArray<StackingEar>,
  angleRad: number,
): StackingEar[] {
  return ears.map(e => ({
    ...e,
    azimuthRad: e.azimuthRad + angleRad,
    outline: rotatePoints(e.outline, angleRad),
    holes: rotatePoints(e.holes, angleRad),
  }));
}

/** Plain outer-circle outline (no ears): the honest-drop fallback shape.
 *  Sampled at the house density, one closed simple loop. */
function plainCircleOutline(params: StatorParams): Array<[number, number]> {
  const R = params.outerRadius;
  const loop: Array<[number, number]> = [];
  for (let i = 0; i < OUTLINE_ARC_SAMPLES; i++) {
    const a = (2 * Math.PI * i) / OUTLINE_ARC_SAMPLES;
    loop.push([R * Math.cos(a), R * Math.sin(a)]);
  }
  loop.push(loop[0]);
  return loop;
}

/**
 * Splice the ear outlines into the board's outer circle, producing ONE closed
 * simple loop. The circle is sampled at OUTLINE_ARC_SAMPLES/2π; the arc span
 * under each ear is replaced by that ear's outline (radial out, outer arc,
 * radial in). Ears are walked in ascending azimuth; between consecutive ears
 * the bare circle is sampled at the board outer radius. The returned polyline
 * repeats its first vertex as its last (explicit closure).
 *
 * HONEST-DROP: if any two ears' sectors overlap (a collision — see
 * `assessEarLayout`), splicing them would emit a self-intersecting bowtie. This
 * returns the plain outer circle instead (still one closed simple loop); callers
 * read the collision + reason from `assessEarLayout`. Never emits a bad outline.
 */
export function buildOutlineWithEars(
  params: StatorParams,
  ears: StackingEar[],
): Array<[number, number]> {
  if (firstOverlap(ears)) return plainCircleOutline(params);

  const R = params.outerRadius;
  const half = (EAR_ARC_DEG / 2) * DEG;

  // Sort by centerline azimuth in [0, 2π); each ear spans [azimuth ± half].
  const sorted = [...ears].sort((a, b) => norm2pi(a.azimuthRad) - norm2pi(b.azimuthRad));

  const loop: Array<[number, number]> = [];
  for (let i = 0; i < sorted.length; i++) {
    const ear = sorted[i];
    const next = sorted[(i + 1) % sorted.length];
    // Ear outline: base-left … base-right (endpoints sit on the outer circle).
    for (const p of ear.outline) loop.push(p);

    // Bare-circle gap from this ear's right base to the next ear's left base.
    const gapStart = norm2pi(ear.azimuthRad) + half;
    let gapEnd = norm2pi(next.azimuthRad) - half;
    while (gapEnd <= gapStart) gapEnd += 2 * Math.PI;      // wrap forward
    const segs = Math.max(1, Math.round((OUTLINE_ARC_SAMPLES * (gapEnd - gapStart)) / (2 * Math.PI)));
    for (let s = 1; s < segs; s++) {                        // interior points only; bases already placed
      const a = gapStart + ((gapEnd - gapStart) * s) / segs;
      loop.push([R * Math.cos(a), R * Math.sin(a)]);
    }
  }
  loop.push(loop[0]);   // explicit closure
  return loop;
}
